import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  CreateFoodicsBranchMappingBody,
  FoodicsBranchMapping,
  FoodicsBranchMappingsResponse,
  FoodicsBranchesResponse,
  FoodicsIntegrationStatus,
  FoodicsMenuEntitiesResponse,
  FoodicsOrdersPreviewResponse,
  FoodicsSuppliersResponse,
  ImportFoodicsMenuEntitiesBody,
  ImportFoodicsOrdersBody,
  ImportFoodicsOrdersResponse,
  ImportFoodicsSuppliersResponse,
} from "src/types/foodics";
import { APIErrorResponse } from "src/types/generic";
import apiClient from "../apiClient";

// API Functions //

/**
 * apiClient resolves to the response body, which the API's ApiResponseTrait
 * still wraps as { data, errors }. Every type below describes the payload
 * inside that wrapper, so each call has to peel it off.
 */
const unwrap = async <T>(request: Promise<unknown>): Promise<T> =>
  ((await request) as { data: T }).data;

const getFoodicsStatus = async (): Promise<FoodicsIntegrationStatus> => {
  return unwrap<FoodicsIntegrationStatus>(apiClient.get("/api/foodics/status"));
};

const disconnectFoodics = async (): Promise<{ message: string }> => {
  return unwrap<{ message: string }>(apiClient.post("/api/foodics/disconnect"));
};

const getFoodicsBranches = async (): Promise<FoodicsBranchesResponse> => {
  return unwrap<FoodicsBranchesResponse>(apiClient.get("/api/foodics/branches"));
};

const getFoodicsBranchMappings =
  async (): Promise<FoodicsBranchMappingsResponse> => {
    return unwrap<FoodicsBranchMappingsResponse>(
      apiClient.get("/api/foodics/mappings/branches")
    );
  };

const createFoodicsBranchMapping = async (
  data: CreateFoodicsBranchMappingBody
): Promise<{ message: string; data: FoodicsBranchMapping }> => {
  return unwrap<{ message: string; data: FoodicsBranchMapping }>(
    apiClient.post("/api/foodics/mappings/branches", data)
  );
};

const deleteFoodicsBranchMapping = async (
  id: number
): Promise<{ message: string }> => {
  return unwrap<{ message: string }>(
    apiClient.delete(`/api/foodics/mappings/branches/${id}`)
  );
};

const getFoodicsMenuEntities = async (params?: {
  products_page?: number;
  products_per_page?: number;
  categories_page?: number;
  categories_per_page?: number;
  include_inventory?: boolean | 0 | 1;
  include_product_ingredients?: boolean | 0 | 1;
  include_modifier_options?: boolean | 0 | 1;
  modifier_options_page?: number;
  modifier_options_per_page?: number;
  inventory_page?: number;
  inventory_per_page?: number;
}): Promise<FoodicsMenuEntitiesResponse> => {
  return unwrap<FoodicsMenuEntitiesResponse>(
    apiClient.get("/api/foodics/menu/entities", { params })
  );
};

const importFoodicsMenuEntities = async (
  data: ImportFoodicsMenuEntitiesBody
): Promise<{
  message: string;
  data: {
    imported_categories: number;
    imported_products: number;
    imported_modifier_options?: number;
    imported_inventory_items?: number;
    skipped_inventory_items?: number;
    synced_production_recipes?: number;
    menu_recipes_synced?: number;
  };
}> => {
  return unwrap(apiClient.post("/api/foodics/menu/import", data));
};

const getFoodicsOrdersPreview = async (params?: {
  branch_id?: string;
  start_date?: string;
  end_date?: string;
  page?: number;
  per_page?: number;
}): Promise<FoodicsOrdersPreviewResponse> => {
  return unwrap<FoodicsOrdersPreviewResponse>(
    apiClient.get("/api/foodics/orders", { params })
  );
};

const importFoodicsOrders = async (
  data: ImportFoodicsOrdersBody
): Promise<ImportFoodicsOrdersResponse> => {
  return unwrap<ImportFoodicsOrdersResponse>(
    apiClient.post("/api/foodics/orders/import", data)
  );
};

const importFoodicsSuppliers = async (): Promise<ImportFoodicsSuppliersResponse> => {
  return unwrap<ImportFoodicsSuppliersResponse>(
    apiClient.post("/api/foodics/suppliers/import")
  );
};

const getFoodicsSuppliers = async (params?: {
  page?: number;
  per_page?: number;
}): Promise<FoodicsSuppliersResponse> => {
  return unwrap<FoodicsSuppliersResponse>(
    apiClient.get("/api/foodics/suppliers", { params })
  );
};

// React Query Hooks //

export const useFoodicsStatus = () =>
  useQuery<FoodicsIntegrationStatus, APIErrorResponse>({
    queryKey: ["foodics-status"],
    queryFn: getFoodicsStatus,
  });

export const useDisconnectFoodics = () =>
  useMutation<{ message: string }, APIErrorResponse, void>({
    mutationFn: disconnectFoodics,
  });

export const useFoodicsBranches = (enabled: boolean = true) =>
  useQuery<FoodicsBranchesResponse, APIErrorResponse>({
    queryKey: ["foodics-branches"],
    queryFn: getFoodicsBranches,
    enabled,
  });

export const useFoodicsBranchMappings = (enabled: boolean = true) =>
  useQuery<FoodicsBranchMappingsResponse, APIErrorResponse>({
    queryKey: ["foodics-branch-mappings"],
    queryFn: getFoodicsBranchMappings,
    enabled,
  });

export const useCreateFoodicsBranchMapping = () =>
  useMutation<
    { message: string; data: FoodicsBranchMapping },
    APIErrorResponse,
    CreateFoodicsBranchMappingBody
  >({
    mutationFn: createFoodicsBranchMapping,
  });

export const useDeleteFoodicsBranchMapping = () =>
  useMutation<{ message: string }, APIErrorResponse, number>({
    mutationFn: deleteFoodicsBranchMapping,
  });

export const useFoodicsMenuEntities = (
  params?: {
    products_page?: number;
    products_per_page?: number;
    categories_page?: number;
    categories_per_page?: number;
    include_inventory?: boolean | 0 | 1;
    include_product_ingredients?: boolean | 0 | 1;
    include_modifier_options?: boolean | 0 | 1;
    modifier_options_page?: number;
    modifier_options_per_page?: number;
    inventory_page?: number;
    inventory_per_page?: number;
  },
  enabled: boolean = true
) =>
  useQuery<FoodicsMenuEntitiesResponse, APIErrorResponse>({
    queryKey: ["foodics-menu-entities", params],
    queryFn: () => getFoodicsMenuEntities(params),
    enabled,
  });

export const useImportFoodicsMenuEntities = () =>
  useMutation<
    {
      message: string;
      data: {
        imported_categories: number;
        imported_products: number;
        imported_modifier_options?: number;
        imported_inventory_items?: number;
        skipped_inventory_items?: number;
        synced_production_recipes?: number;
        menu_recipes_synced?: number;
      };
    },
    APIErrorResponse,
    ImportFoodicsMenuEntitiesBody
  >({
    mutationFn: importFoodicsMenuEntities,
  });

export const useFoodicsOrdersPreview = (
  params?: {
    branch_id?: string;
    start_date?: string;
    end_date?: string;
    page?: number;
    per_page?: number;
  },
  enabled: boolean = false
) =>
  useQuery<FoodicsOrdersPreviewResponse, APIErrorResponse>({
    queryKey: ["foodics-orders-preview", params],
    queryFn: () => getFoodicsOrdersPreview(params),
    enabled,
  });

export const useImportFoodicsOrders = () =>
  useMutation<
    ImportFoodicsOrdersResponse,
    APIErrorResponse,
    ImportFoodicsOrdersBody
  >({
    mutationFn: importFoodicsOrders,
  });

export const useImportFoodicsSuppliers = () =>
  useMutation<ImportFoodicsSuppliersResponse, APIErrorResponse, void>({
    mutationFn: importFoodicsSuppliers,
  });

export const useFoodicsSuppliers = (
  params?: { page?: number; per_page?: number },
  enabled: boolean = true
) =>
  useQuery<FoodicsSuppliersResponse, APIErrorResponse>({
    queryKey: ["foodics-suppliers", params],
    queryFn: () => getFoodicsSuppliers(params),
    enabled,
  });

